// ChartIcons.tsx
//The Component that deals with displaying the icons of the Charts

import React from 'react';
import { chartTypes } from './chartTypes';
import {
  changeGeoKey,
  setOldChartType,
} from '../../../redux/ChartPoperties/ChartPropertiesActions';
import { useDispatch } from 'react-redux';
import {
  addOrEditSimplecardConditionalFormat,
  deleteTablecf,
  enableGrid,
  MeasureValue,
  SortOrder,
  updateChartData,
  updateLabelOption,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { sortChartData } from '../../ChartAxes/chartDataFunctions/sortChartData';

interface ChartIconsProps {
  selectedChart: string;
  propKey: string;
  chartProp: any;
  chartControls: any;
  getAndUpdateNewChartAxes: (oldType: string, newType: string) => void;
  handleAddTile: (chartName: string) => void;
}

const ChartIcons: React.FC<ChartIconsProps> = ({
  selectedChart,
  propKey,
  chartProp,
  chartControls,

  getAndUpdateNewChartAxes,
  handleAddTile,
}) => {
  const dispatch = useDispatch();

  return (
    <>
      {chartTypes.map((chart) => (
        <img
          key={chart.name}
          className={`chartIcon ${chart.name === 'simplecard' ? 'simplecard' : ''} ${
            chart.name === selectedChart ? 'selected' : ''
          }`}
          src={chart.icon}
          alt={chart.name}
          onClick={() => {
            if (
              [
                'multibar',
                'stackedBar',
                'horizontalBar',
                'horizontalStacked',
                'line',
                'pie',
                'donut',
                'rose',
                'area',
                'scatterPlot',
                'funnel',
                'gauge',
                'heatmap',
                'crossTab',
                'table',
                'filledMap',
                'stackedArea',
                'calendar',
                'bubbleMap',
                'boxPlot',
                'treeMap',
                'sankey',
                'richText',
                'simplecard',
                'waterfall',
                'wordCloud',
              ].includes(chart.name)
            ) {
              if (
                [
                  'multibar',
                  'stackedBar',
                  'waterfall',
                  'line',
                  'area',
                  'stackedArea',
                  'boxPlot',
                ].includes(chart.name)
              ) {
                dispatch(enableGrid(propKey, 'ySplitLine', true));
                dispatch(enableGrid(propKey, 'xSplitLine', false));
              } else if (['horizontalBar', 'horizontalStacked'].includes(chart.name)) {
                dispatch(enableGrid(propKey, 'xSplitLine', true));
                dispatch(enableGrid(propKey, 'ySplitLine', false));
              } else {
                dispatch(enableGrid(propKey, 'xSplitLine', true));
                dispatch(enableGrid(propKey, 'ySplitLine', true));
              }
              //currentChartType is the chart which is currently selected and chart.name is the new chart on which user clicks. For ex- If user switches from multibar to piechart then currentChartType === multibar and chart.name === piechart
              const currentChartType = chartProp.properties[propKey].chartType;
              const currentMapType = chartProp.properties[propKey].Geo.geoLocation;

              //---- Reset manual sort when switching to crossTab or table, but keep ascending/descending(This should be removed when manual sort is added in cross tab and table)--------------//
              const sortOrder = chartControls.properties[propKey].sortOrder;
              if (
                (chart.name === 'crossTab' || chart.name === 'table') &&
                sortOrder === 'Manual Sort'
              ) {
                dispatch(SortOrder(propKey, 'Ascending'));
                dispatch(
                  updateChartData(
                    propKey,
                    sortChartData(
                      chartControls.properties[propKey].serverData,
                      chartProp.properties[propKey],
                      chartControls,
                      propKey,
                      undefined,
                      'Ascending',
                    ),
                  ),
                );
              }
              //-------------------------------(End)-------------------------------------------------------//

              if (currentChartType === 'simplecard' && chart.name !== 'simplecard') {
                dispatch(addOrEditSimplecardConditionalFormat(propKey, []));
              }
              if (currentChartType === 'crossTab' || currentChartType === 'table') {
                if (chart.name !== 'crossTab' && chart.name !== 'table') {
                  let tableConditionalFormats =
                    chartControls?.properties[propKey]?.tableConditionalFormats;
                  tableConditionalFormats?.forEach((_: any, index: number) =>
                    dispatch(deleteTablecf(propKey, tableConditionalFormats.length - 1 - index)),
                  );
                }
              }

              const fieldDataType = chartProp.properties[propKey].chartAxes[2]?.fields[0]?.dataType;

              if (chart.name === 'filledMap' || chart.name === 'bubbleMap') {
                if (fieldDataType && fieldDataType !== 'text') {
                }
                dispatch(updateLabelOption(propKey, 'showLabel', false));
                dispatch(updateLabelOption(propKey, 'showStateLabel', false));
              }

              if (chart.name === 'filledMap' && currentMapType.includes('Cities')) {
                chartProp.properties[propKey].Geo.geoLocation = 'world';
                dispatch(changeGeoKey(propKey, 'world'));
              }

              const oldChartAxes = chartProp.properties[propKey].chartAxes;

              if (
                chartProp.properties[propKey].chartType === 'richText' &&
                chart.name === 'richText'
              ) {
                dispatch(setOldChartType(propKey, currentChartType));
                getAndUpdateNewChartAxes(chartProp.properties[propKey].chartType, chart.name);
              } else if (
                chartProp.properties[propKey].chartType === 'richText' &&
                chart.name !== 'richText'
              ) {
                if (
                  chartControls.properties[propKey].richText === '' ||
                  chartControls.properties[propKey].richText === '<p><br></p>'
                ) {
                  dispatch(setOldChartType(propKey, currentChartType));
                  getAndUpdateNewChartAxes(chartProp.properties[propKey].chartType, chart.name);
                } else {
                  handleAddTile(chart.name);
                }
              } else if (
                chartProp.properties[propKey].chartType !== 'richText' &&
                chart.name === 'richText'
              ) {
                let noOfAxes = oldChartAxes.length;
                oldChartAxes.forEach((axes: any) => {
                  if (axes.fields.length === 0) {
                    noOfAxes -= 1;
                  }
                });

                if (noOfAxes === 0) {
                  dispatch(setOldChartType(propKey, currentChartType));
                  getAndUpdateNewChartAxes(chartProp.properties[propKey].chartType, chart.name);
                } else {
                  handleAddTile(chart.name);
                }
              } else {
                dispatch(setOldChartType(propKey, currentChartType));
                //-------------------------------select measure value from the previous chart if it is already selected in showHide, else not.------------------------------------//
                const measureAxis = chartProp.properties[propKey].chartAxes.find(
                  (axis: any) =>
                    axis.name === 'Measure' || axis.name === 'X Axis' || axis.name === 'Y Axis',
                );
                const showHideMeasureName = chartControls.properties[propKey].showHide.measureName;
                measureAxis?.fields.length > 0 &&
                  showHideMeasureName &&
                  dispatch(MeasureValue(propKey, measureAxis?.fields[0]?.uId));
                //----------------------------------------------------------------------------------------//
                getAndUpdateNewChartAxes(chartProp.properties[propKey].chartType, chart.name);
              }
            }
          }}
          title={chart.value}
        />
      ))}
    </>
  );
};

export default ChartIcons;
